import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentRequestService, projectService } from '@smartgrow/shared';
import farmOperationService from '@smartgrow/shared/api/services/farmOperation.service';
import contractService from '@smartgrow/shared/api/services/contract.service';
import type { CreatePaymentRequestRequest, CreateProcurementRequestRequest, GeneralPaymentRequestRequest } from '@smartgrow/shared/types';

const initialForm: CreatePaymentRequestRequest = {
  requestType: 'CONTRACTOR',
  amount: 0,
  description: '',
  notes: '',
  contractId: undefined,
};

const initialProcurementForm: CreateProcurementRequestRequest = {
  procurementItem: '',
  supplierName: '',
  amount: 0,
  description: '',
  notes: '',
  farmOperationId: undefined,
  operationTaskId: undefined,
};

const initialGeneralLinks = {
  projectId: undefined as string | undefined,
  farmOperationId: undefined as string | undefined,
  operationTaskId: undefined as string | undefined,
};

export const NewPaymentRequestPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<CreatePaymentRequestRequest>(initialForm);
  const [procurementForm, setProcurementForm] = useState<CreateProcurementRequestRequest>(initialProcurementForm);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const isProcurement = form.requestType === 'PROCUREMENT';
  const isOther = form.requestType === 'OTHER';
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [generalLinks, setGeneralLinks] = useState(initialGeneralLinks);

  const { data: contracts } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => contractService.getAll(),
    enabled: form.requestType === 'CONTRACTOR',
  });

  const isContractor = form.requestType === 'CONTRACTOR';
  const selectedContract = contracts?.content?.find((c) => c.id === form.contractId);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: isProcurement || isOther,
  });
  const projects = projectsData?.content ?? [];

  const { data: operationsData } = useQuery({
    queryKey: ['farm-operations', selectedProjectId],
    queryFn: () => farmOperationService.getByProject(selectedProjectId),
    enabled: (isProcurement || isOther) && !!selectedProjectId,
  });
  const operations = operationsData?.content ?? [];

  const { data: contractorOperationsData } = useQuery({
    queryKey: ['farm-operations', selectedContract?.projectId],
    queryFn: () => farmOperationService.getByProject(selectedContract!.projectId),
    enabled: isContractor && !!selectedContract,
  });
  const contractorOperations = contractorOperationsData?.content ?? [];

  const { data: tasks } = useQuery({
    queryKey: ['operation-tasks', procurementForm.farmOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(procurementForm.farmOperationId!),
    enabled: isProcurement && !!procurementForm.farmOperationId,
  });

  const { data: contractorTasks } = useQuery({
    queryKey: ['operation-tasks', form.farmOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(form.farmOperationId!),
    enabled: isContractor && !!form.farmOperationId,
  });

  const { data: generalTasks } = useQuery({
    queryKey: ['operation-tasks', generalLinks.farmOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(generalLinks.farmOperationId!),
    enabled: isOther && !!generalLinks.farmOperationId,
  });

  const mutation = useMutation({
    mutationFn: (data: CreatePaymentRequestRequest) => {
      if (data.requestType === 'CONTRACTOR') {
        return paymentRequestService.createContractor(data);
      }
      if (data.requestType === 'PROCUREMENT') {
        return paymentRequestService.createProcurement(procurementForm);
      }
      const generalData: GeneralPaymentRequestRequest = {
        requestType: data.requestType,
        amount: data.amount,
        description: data.description,
        notes: data.notes,
        projectId: generalLinks.projectId,
        farmOperationId: generalLinks.farmOperationId,
        operationTaskId: generalLinks.operationTaskId,
      };
      return paymentRequestService.createGeneral(generalData);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      navigate('/dashboard/payments/requests');
    },
  });

  const validate = () => {
    const e: Record<string, string> = {};
    if (isProcurement) {
      if (!procurementForm.procurementItem) e.procurementItem = 'Procurement item is required';
      if (!procurementForm.amount || procurementForm.amount <= 0) e.amount = 'Amount must be greater than 0';
      if (!procurementForm.description.trim()) e.description = 'Description is required';
    } else {
      if (!form.description.trim()) e.description = 'Description is required';
      if (!form.amount || form.amount <= 0) e.amount = 'Amount must be greater than 0';
      if (form.requestType === 'CONTRACTOR' && !form.contractId) e.contractId = 'Contract is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    mutation.mutate(form);
  };

  const set = (field: keyof CreatePaymentRequestRequest, value: any) => {
    setForm((f) => {
      const next = { ...f, [field]: value };
      if (field === 'contractId') {
        next.farmOperationId = undefined;
        next.operationTaskId = undefined;
      }
      if (field === 'farmOperationId') {
        next.operationTaskId = undefined;
      }
      return next;
    });
    if (field === 'requestType') {
      setSelectedProjectId('');
      setGeneralLinks(initialGeneralLinks);
    }
    setErrors((e) => { const n = { ...e }; delete n[field as string]; return n; });
  };

  const setP = (field: keyof CreateProcurementRequestRequest, value: any) => {
    setProcurementForm((f) => {
      const next = { ...f, [field]: value };
      if (field === 'farmOperationId') next.operationTaskId = undefined;
      return next;
    });
    setErrors((e) => { const n = { ...e }; delete n[field as string]; return n; });
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">New Payment Request</Typography>
      </Box>

      <Paper sx={{ p: 3, maxWidth: 680 }}>
        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to submit request. Please try again.
          </Alert>
        )}

        <Typography variant="h6" sx={{ mb: 2 }}>Request Details</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            select
            label="Request Type"
            value={form.requestType}
            onChange={(e) => set('requestType', e.target.value)}
            fullWidth
          >
            <MenuItem value="CONTRACTOR">Contractor Payment</MenuItem>
            <MenuItem value="PROCUREMENT">Procurement Payment</MenuItem>
            <MenuItem value="OTHER">Other</MenuItem>
          </TextField>

          {form.requestType === 'CONTRACTOR' && (
            <TextField
              select
              label="Contract"
              value={form.contractId ?? ''}
              onChange={(e) => set('contractId', e.target.value || undefined)}
              fullWidth
              error={!!errors.contractId}
              helperText={errors.contractId}
            >
              {contracts?.content?.length ? (
                contracts.content.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.contractNumber} — {c.contractorCompanyName} ({c.projectName}) · ₦{c.pendingAmount.toLocaleString()} pending
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No active contracts found</MenuItem>
              )}
            </TextField>
          )}

          {isContractor && selectedContract && (
            <>
              <TextField
                label="Project"
                value={selectedContract.projectName}
                fullWidth
                disabled
                helperText="Derived from the selected contract"
              />

              <TextField
                select
                label="Link to Operation (optional)"
                value={form.farmOperationId ?? ''}
                onChange={(e) => set('farmOperationId', e.target.value || undefined)}
                fullWidth
                helperText="Optional"
              >
                <MenuItem value="">— No operation —</MenuItem>
                {contractorOperations.map((op) => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.name} ({op.operationType.replace(/_/g, ' ')})
                  </MenuItem>
                ))}
              </TextField>

              {form.farmOperationId && (
                <TextField
                  select
                  label="Link to Task (optional)"
                  value={form.operationTaskId ?? ''}
                  onChange={(e) => set('operationTaskId', e.target.value || undefined)}
                  fullWidth
                  helperText="Optional"
                >
                  <MenuItem value="">— No task —</MenuItem>
                  {contractorTasks?.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.title} ({t.taskType.replace(/_/g, ' ')})
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}

          {isProcurement && (
            <>
              <TextField
                label="Procurement Item"
                value={procurementForm.procurementItem}
                onChange={(e) => setP('procurementItem', e.target.value)}
                fullWidth
                error={!!errors.procurementItem}
                helperText={errors.procurementItem}
                inputProps={{ maxLength: 200 }}
              />

              <TextField
                label="Supplier Name (optional)"
                value={procurementForm.supplierName ?? ''}
                onChange={(e) => setP('supplierName', e.target.value || undefined)}
                fullWidth
                inputProps={{ maxLength: 200 }}
              />

              <TextField
                select
                label="Project (optional)"
                value={selectedProjectId}
                onChange={(e) => {
                  setSelectedProjectId(e.target.value);
                  setP('farmOperationId', undefined);
                }}
                fullWidth
                helperText="Select project to filter operations"
              >
                <MenuItem value="">— No project —</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Link to Operation (optional)"
                value={procurementForm.farmOperationId ?? ''}
                onChange={(e) => setP('farmOperationId', e.target.value || undefined)}
                fullWidth
                disabled={!selectedProjectId}
                helperText={!selectedProjectId ? 'Select a project first' : 'Leave blank to auto-create a new operation'}
              >
                <MenuItem value="">— No operation (auto-create) —</MenuItem>
                {operations.map((op) => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.name} ({op.operationType.replace(/_/g, ' ')})
                  </MenuItem>
                ))}
              </TextField>

              {procurementForm.farmOperationId && (
                <TextField
                  select
                  label="Link to Task (optional)"
                  value={procurementForm.operationTaskId ?? ''}
                  onChange={(e) => setP('operationTaskId', e.target.value || undefined)}
                  fullWidth
                  helperText="Leave blank to auto-create a new task"
                >
                  <MenuItem value="">— No task (auto-create) —</MenuItem>
                  {tasks?.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.title} ({t.taskType.replace(/_/g, ' ')})
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                label="Amount (₦)"
                type="number"
                value={procurementForm.amount || ''}
                onChange={(e) => setP('amount', Number.parseFloat(e.target.value) || 0)}
                fullWidth
                error={!!errors.amount}
                helperText={errors.amount}
                inputProps={{ min: 0.01, step: 0.01 }}
              />

              <TextField
                label="Description"
                multiline
                rows={3}
                value={procurementForm.description}
                onChange={(e) => setP('description', e.target.value)}
                fullWidth
                error={!!errors.description}
                helperText={errors.description}
                inputProps={{ maxLength: 2000 }}
              />

              <TextField
                label="Notes (optional)"
                multiline
                rows={2}
                value={procurementForm.notes ?? ''}
                onChange={(e) => setP('notes', e.target.value || undefined)}
                fullWidth
                inputProps={{ maxLength: 1000 }}
              />
            </>
          )}

          {!isProcurement && (
            <>
              {isOther && (
                <>
                  <TextField
                    select
                    label="Project (optional)"
                    value={selectedProjectId}
                    onChange={(e) => {
                      setSelectedProjectId(e.target.value);
                      setGeneralLinks((l) => ({ ...l, projectId: e.target.value || undefined, farmOperationId: undefined, operationTaskId: undefined }));
                    }}
                    fullWidth
                    helperText="Select project to filter operations"
                  >
                    <MenuItem value="">— No project —</MenuItem>
                    {projects.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Link to Operation (optional)"
                    value={generalLinks.farmOperationId ?? ''}
                    onChange={(e) => setGeneralLinks((l) => ({ ...l, farmOperationId: e.target.value || undefined, operationTaskId: undefined }))}
                    fullWidth
                    disabled={!selectedProjectId}
                    helperText={!selectedProjectId ? 'Select a project first' : 'Optional'}
                  >
                    <MenuItem value="">— No operation —</MenuItem>
                    {operations.map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.name} ({op.operationType.replace(/_/g, ' ')})
                      </MenuItem>
                    ))}
                  </TextField>

                  {generalLinks.farmOperationId && (
                    <TextField
                      select
                      label="Link to Task (optional)"
                      value={generalLinks.operationTaskId ?? ''}
                      onChange={(e) => setGeneralLinks((l) => ({ ...l, operationTaskId: e.target.value || undefined }))}
                      fullWidth
                      helperText="Optional"
                    >
                      <MenuItem value="">— No task —</MenuItem>
                      {generalTasks?.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.title} ({t.taskType.replace(/_/g, ' ')})
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </>
              )}

              <TextField
                label="Amount (₦)"
                type="number"
                value={form.amount || ''}
                onChange={(e) => set('amount', Number.parseFloat(e.target.value) || 0)}
                fullWidth
                error={!!errors.amount}
                helperText={errors.amount}
                inputProps={{ min: 0.01, step: 0.01 }}
              />

              <TextField
                label="Description"
                multiline
                rows={3}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                fullWidth
                error={!!errors.description}
                helperText={errors.description}
                inputProps={{ maxLength: 2000 }}
              />

              <TextField
                label="Notes (optional)"
                multiline
                rows={2}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                fullWidth
                inputProps={{ maxLength: 1000 }}
              />
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate('/dashboard/payments/requests')} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Submit Request
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
